import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  X, Phone, Clock, Smartphone, ArrowRight, UserCheck, Heart,
  MessageSquare,
} from 'lucide-react';
import { useGetCallLogQuery, useMarkCallReviewedMutation } from '../../api/callLogsApi';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../utils/apiError';
import { openPatientDetail } from '../ui/detailPanelSlice';

export default function CallLogDetailDrawer({ id, onClose, onConvert }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading, isError, refetch } = useGetCallLogQuery(id, { skip: !id });
  const [markReviewed] = useMarkCallReviewedMutation();

  if (!id) return null;

  const handleMarkReviewed = async () => {
    try { await markReviewed(id).unwrap(); toast.success('Marked as reviewed'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Failed')); }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Phone size={18} /> {data?.phoneNumberRaw || 'Call Log'}
          </h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {isLoading && <div className="skeleton" style={{ height: 80 }} />}
          {isError && (
            <div className="auth-error">
              Could not load call details. <button className="btn btn-sm btn-outline" onClick={refetch}>Retry</button>
            </div>
          )}

          {data && (
            <>
              {/* Summary */}
              <div className="card card-body">
                <div className="info-row"><div className="ir-label">Caller</div><div className="ir-value">{data.callerName || 'Unknown'}</div></div>
                <div className="info-row"><div className="ir-label">Total calls</div><div className="ir-value"><strong>{data.totalCalls}</strong></div></div>
                <div className="info-row"><div className="ir-label">Last call</div><div className="ir-value">{new Date(data.lastCallAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div></div>
                <div className="info-row"><div className="ir-label">First call</div><div className="ir-value">{new Date(data.firstCallAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div></div>
                {data.sourceDevice && (
                  <div className="info-row"><div className="ir-label">Device</div><div className="ir-value"><Smartphone size={12} /> {data.sourceDevice}</div></div>
                )}
                <div className="info-row"><div className="ir-label">Status</div><div className="ir-value">
                  {data.status === 'Converted' && <span className="badge b-green">Converted</span>}
                  {data.isDiscarded && <span className="badge b-gray">Discarded</span>}
                  {!data.isDiscarded && data.status === 'Reviewed' && <span className="badge b-blue">Reviewed</span>}
                  {!data.isDiscarded && data.status === 'New' && <span className="badge b-amber">New</span>}
                </div></div>
              </div>

              {/* Existing record indicators */}
              {(data.isExistingPatient || data.isExistingLead) && (
                <>
                  <div className="cl-drawer-section-title">Matched Existing Records</div>
                  {data.isExistingPatient && (
                    <div className="cl-existing-card patient">
                      <div className="cl-existing-card-info">
                        <div className="cl-existing-card-label"><Heart size={11} /> Existing Patient</div>
                        <div className="cl-existing-card-name">{data.existingPatientName}</div>
                        <div className="cl-existing-card-sub">{data.existingPatientCode}</div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => { dispatch(openPatientDetail(data.existingPatientId)); onClose(); }}
                      >
                        View
                      </button>
                    </div>
                  )}
                  {data.isExistingLead && (
                    <div className="cl-existing-card">
                      <div className="cl-existing-card-info">
                        <div className="cl-existing-card-label"><UserCheck size={11} /> Existing Lead</div>
                        <div className="cl-existing-card-name">{data.existingLeadName}</div>
                        <div className="cl-existing-card-sub">Status: {data.existingLeadStatus}</div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => { navigate(`/leads?openId=${data.existingLeadId}`); onClose(); }}
                      >
                        View
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Actions */}
              <div className="cl-drawer-section-title">Actions</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {/* Intentionally independent of isRoutedToOther — "Sent to Other" must not be a
                    dead end; staff can still convert the call to a lead afterwards. */}
                {data.status !== 'Converted' && !data.isExistingPatient && !data.isExistingLead && (
                  <button className="btn btn-sm btn-success" onClick={() => onConvert?.(data)}>
                    <ArrowRight size={12} /> Convert to Lead
                  </button>
                )}
                {data.status === 'New' && (
                  <button className="btn btn-sm btn-outline" onClick={handleMarkReviewed}>
                    Mark reviewed
                  </button>
                )}
                <a className="btn btn-sm btn-outline" href={`tel:${data.phoneNumberRaw}`}>
                  <Phone size={12} /> Call back
                </a>
                <a className="btn btn-sm btn-outline" href={`https://wa.me/${data.phoneNumber.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
                  <MessageSquare size={12} /> WhatsApp
                </a>
              </div>

              {/* Call history timeline */}
              <div className="cl-drawer-section-title">Call History ({data.history.length})</div>
              <div className="timeline">
                {data.history.map((h) => (
                  <div key={h.id} className="tl-item">
                    <div className="tl-dot" style={{ background: 'var(--blue-50)', color: 'var(--blue-700)' }}>
                      <Clock size={14} />
                    </div>
                    <div className="tl-line" />
                    <div className="tl-content">
                      <div className="tl-text">{new Date(h.calledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div>
                      {h.sourceDevice && (
                        <div className="tl-time"><Smartphone size={10} /> {h.sourceDevice}</div>
                      )}
                      {h.notes && <div className="tl-time">{h.notes}</div>}
                    </div>
                  </div>
                ))}
                {data.history.length === 0 && (
                  <div className="empty-state"><p>No history entries.</p></div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
